import { desktopUpdateTranslations } from './desktopUpdates.js';
import { desktopDownloadTranslations } from './desktopDownloads.js';
import { CURRENCIES, currencyName } from '../currencies.js';
import { invoicePdfTranslations } from './invoicePdf.js';
import { scannerTranslations } from './scanner.js';
import { agentPaymentTranslations } from './agentPayments.js';
import { he } from './he.js';
import { en } from './en.js';
import type { AuditAction, AuditEntityType, Dictionary, InterpolationVars, TranslationKey } from './types.js';

export type { AuditAction, AuditEntityType, Dictionary, TranslationKey, InterpolationVars };
export { he, en };

export const LOCALES = ['he', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

/** Hebrew-native product: `he` is both the default and the source locale. */
export const DEFAULT_LOCALE: Locale = 'he';

export const LOCALE_DIR: Record<Locale, 'rtl' | 'ltr'> = {
  he: 'rtl',
  en: 'ltr',
};

/** Endonyms — a language picker should always read in its own language. */
export const LOCALE_LABELS: Record<Locale, string> = {
  he: 'עברית',
  en: 'English',
};

const currencyTranslations = (locale: Locale) => ({
  ...Object.fromEntries(CURRENCIES.map(code => [code, currencyName(code, locale)])),
  description: '{{code}} — {{name}}',
});
const scanTranslations = {
  he: {
    scanQueued: 'ממתין לסורק פנוי',
    scanRefining: 'משלים פענוח מסמכים שנשמרו',
    scanRecovering: 'ממשיך את הסריקה מהמקום שבו נעצרה',
    syncCanceled: 'הסריקה בוטלה בעקבות ניתוק החשבון',
  },
  en: {
    scanQueued: 'Waiting for an available scanner',
    scanRefining: 'Finishing saved document processing',
    scanRecovering: 'Resuming the saved scan',
    syncCanceled: 'Scan canceled because the mailbox was disconnected',
  },
};
const localizedHe = { ...he, history: {...he.history, viewCurrentFolder:'צפייה בתיקייה הנוכחית', currentFolderNotice:'תוכן התיקייה הנוכחי — ייתכן שהוא שונה מהשליחה המקורית.', currentFolderFiles:'קבצים בתיקייה כיום ({{count}})', currentFolderEmpty:'התיקייה ריקה כרגע.'}, desktopDownload: desktopDownloadTranslations.he, scanner: scannerTranslations.he, desktopUpdate: desktopUpdateTranslations.he, invoices: { ...he.invoices, ...scanTranslations.he }, currencies: currencyTranslations('he'),
  agentUi: agentPaymentTranslations.he.agentUi, agentOperations: agentPaymentTranslations.he.agentOperations, agentFields: agentPaymentTranslations.he.agentFields,
  mail: { ...he.mail, ...agentPaymentTranslations.he.mail },
  invoiceScan: { ...he.invoiceScan, ...invoicePdfTranslations.he.invoiceScan },
  validation: { ...he.validation, invoice: { ...he.validation.invoice, ...invoicePdfTranslations.he.validation } } };
const localizedEn = { ...en, history: {...en.history, viewCurrentFolder:'View current folder', currentFolderNotice:'Current folder contents — these may differ from the original shipment.', currentFolderFiles:'Current folder files ({{count}})', currentFolderEmpty:'The folder is currently empty.'}, desktopDownload: desktopDownloadTranslations.en, scanner: scannerTranslations.en, desktopUpdate: desktopUpdateTranslations.en, invoices: { ...en.invoices, ...scanTranslations.en }, currencies: currencyTranslations('en'),
  agentUi: agentPaymentTranslations.en.agentUi, agentOperations: agentPaymentTranslations.en.agentOperations, agentFields: agentPaymentTranslations.en.agentFields,
  mail: { ...en.mail, ...agentPaymentTranslations.en.mail },
  invoiceScan: { ...en.invoiceScan, ...invoicePdfTranslations.en.invoiceScan },
  validation: { ...en.validation, invoice: { ...en.validation.invoice, ...invoicePdfTranslations.en.validation } } };

export const dictionaries: Record<Locale, Dictionary> = {
  he: localizedHe as unknown as Dictionary,
  en: localizedEn as unknown as Dictionary,
};

/** i18next `resources` payload; the web app feeds this straight into init(). */
export const resources = {
  he: { translation: localizedHe },
  en: { translation: localizedEn },
} as const;

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/** Picks the best supported locale from an Accept-Language header. */
export function resolveLocale(header: string | undefined | null): Locale {
  if (!header) return DEFAULT_LOCALE;
  for (const part of header.split(',')) {
    const tag = part.split(';')[0]?.trim().toLowerCase() ?? '';
    const base = tag.split('-')[0];
    if (isLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}

function lookup(dict: Dictionary, key: string): string | undefined {
  let node: unknown = dict;
  for (const segment of key.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = (node as Record<string, unknown>)[segment];
  }
  return typeof node === 'string' ? node : undefined;
}

function interpolate(template: string, vars?: InterpolationVars): string {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

/**
 * Server-side translator (emails, PDFs, notification bodies).
 * The web app uses react-i18next instead, but both read the same dictionaries.
 * Falls back to Hebrew, then to the raw key — never renders `undefined`.
 */
export function t(locale: Locale, key: TranslationKey | string, vars?: InterpolationVars): string {
  /*
   * `key_one` is i18next's singular suffix, which the web app already relies
   * on; the same dictionary is read here, so the suffix is honoured here too.
   * Without it a mail about a single invoice said "1 invoices" — rare while a
   * share was always a selection, ordinary now that a row sends itself.
   * A key with no singular of its own simply falls through to the plain one.
   */
  const candidates = vars?.count === 1 ? [`${key}_one`, key] : [key];
  // The whole locale is tried before falling back, so an English singular that
  // does not exist yields the English plural rather than the Hebrew singular.
  const pick = (dict: Dictionary): string | undefined =>
    candidates.reduce<string | undefined>((found, candidate) => found ?? lookup(dict, candidate), undefined);
  const template = pick(dictionaries[locale]) ?? pick(dictionaries[DEFAULT_LOCALE]) ?? key;
  return interpolate(template, vars);
}

/** Curried form, convenient inside a request scope: `const tr = translator(locale)`. */
export function translator(locale: Locale) {
  return (key: TranslationKey | string, vars?: InterpolationVars) => t(locale, key, vars);
}
